import { useAuth } from 'src/auth/AuthContext';
import { Link, Navigate, Outlet, useLocation, useOutletContext, useParams } from 'react-router-dom';
import { Avatar, Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import { PermissionRole } from '../../constants';
import { useLocationDetail, useLocations } from 'src/queries/locations';
import { Merchant } from 'src/interfaces/interfaces';

interface LocationOutletContext {
  merchant: Merchant;
}

/**
 * The active location, for the tabs rendered inside this route.
 *
 * Handed down through the outlet rather than re-queried per child so the
 * non-null guarantee is carried by the type: this route does not render its
 * children until the record has loaded, so a tab can never observe a half-loaded
 * or previous location. Components outside this subtree (the sidebar, the
 * support dialog) use `useActiveLocation` instead and handle `undefined`.
 */
export const useCurrentLocation = () => useOutletContext<LocationOutletContext>();

const headingFont = "'Bricolage Grotesque', sans-serif";
const bodyFont = "'Instrument Sans', system-ui, sans-serif";

const subscriptionPill = (status?: string) => {
  switch (status) {
    case 'active':
      return { label: 'Active', bg: '#E3F1E6', fg: '#1F6B3A' };
    case 'free_trial':
      return { label: 'Free trial', bg: '#E3F1E6', fg: '#1F6B3A' };
    case 'payment_overdue':
      return { label: 'Payment overdue', bg: '#FBEFD9', fg: '#8A5A00' };
    default:
      return { label: 'Needs setup', bg: '#FBEFD9', fg: '#8A5A00' };
  }
};

const CenteredSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
    <CircularProgress />
  </Box>
);

/**
 * Guard + layout route for /locations/:id/*. The URL is the single source of
 * truth for which location is active: the :id param is synced into the
 * merchants slice, and the existing listener middleware loads the full
 * location details. Children render only once those details match the URL,
 * so sub-pages never see the previously selected location's data.
 */
const LocationRoute = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const locationId = Number(id);
  const isValidId = Number.isInteger(locationId) && locationId > 0;

  const userId = useAuth().id;
  const isSuperAdmin = useAuth().isSuperAdmin;

  const locations = useLocations(userId);
  // One query per location id. The guarantee that a tab never renders the
  // previously selected location's data used to be enforced by comparing
  // `merchant.id !== locationId` against a single shared slice; now it is
  // structural — a different id is a different cache entry, so the wrong
  // location's data is not reachable from here.
  const location = useLocationDetail(isValidId ? locationId : undefined);

  const linkedLocation = locations.data?.find((item) => item.id === locationId);
  const hasAccess =
    isSuperAdmin || linkedLocation?.userPermissionRole === PermissionRole.Admin;

  if (!isValidId) {
    return <Navigate to="/locations" replace />;
  }

  // `isPending` is precisely the old `!merchants.loaded`: no data yet, as
  // distinct from refetching. Access cannot be judged before the list arrives.
  if (locations.isPending) {
    return <CenteredSpinner />;
  }

  if (!hasAccess) {
    return <Navigate to="/locations" replace />;
  }

  if (location.isError) {
    return <Navigate to="/locations" replace />;
  }

  if (!location.data) {
    return <CenteredSpinner />;
  }

  const merchant = location.data;

  const base = `/locations/${locationId}`;
  const tabs = [
    { label: 'Details', path: base },
    { label: 'Analytics', path: `${base}/analytics` },
    { label: 'Manage Team', path: `${base}/team` },
    { label: 'Subscription', path: `${base}/subscription` },
    { label: 'Digital Flyers', path: `${base}/digital-flyers` },
  ];
  const activeTab = Math.max(0, tabs.findIndex((tab) => tab.path === pathname));
  const pill = subscriptionPill(merchant.subscription?.status);

  return (
    <Box
      sx={{
        maxWidth: 1080,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}
    >
      <ButtonBase
        component={Link}
        to='/locations'
        sx={{
          fontFamily: bodyFont,
          fontWeight: 500,
          fontSize: 14,
          color: '#5F6B76',
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          alignSelf: 'flex-start',
          borderRadius: '8px',
          '&:hover': { color: '#17222C' }
        }}
      >
        <ChevronLeft sx={{ fontSize: 18 }} />
        Locations
      </ButtonBase>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
        <Avatar
          src={merchant.logoUrl}
          alt={merchant.name}
          variant='rounded'
          sx={{
            width: 72,
            height: 72,
            borderRadius: '18px',
            backgroundColor: '#17222C',
            fontFamily: headingFont,
            fontWeight: 700,
            fontSize: 24
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <Typography
              component='h1'
              sx={{
                fontFamily: headingFont,
                fontWeight: 700,
                fontSize: 34,
                lineHeight: 1.05,
                letterSpacing: '-.02em',
                color: '#17222C'
              }}
            >
              {linkedLocation?.name ?? merchant.name}
            </Typography>
            <Box
              component='span'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: 26,
                px: '10px',
                borderRadius: '99px',
                backgroundColor: pill.bg,
                color: pill.fg,
                fontFamily: bodyFont,
                fontWeight: 500,
                fontSize: 13
              }}
            >
              <Box
                component='span'
                sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'currentColor' }}
              />
              {pill.label}
            </Box>
          </Box>
          {merchant.location && (
            <Typography sx={{ fontFamily: bodyFont, fontSize: 15, color: '#5F6B76' }}>
              {merchant.location}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'inline-flex',
          alignSelf: 'flex-start',
          border: '1px solid #CBD8E1',
          borderRadius: '10px',
          backgroundColor: '#fff',
          p: '3px',
          gap: '2px',
          flexWrap: 'wrap'
        }}
      >
        {tabs.map((tab, index) => {
          const active = index === activeTab;
          return (
            <ButtonBase
              key={tab.path}
              component={Link}
              to={tab.path}
              sx={{
                px: '12px',
                height: 34,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '7px',
                backgroundColor: active ? '#17222C' : 'transparent',
                color: active ? '#fff' : '#46525C',
                fontFamily: bodyFont,
                fontWeight: 500,
                fontSize: 14,
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: active ? '#17222C' : '#EEF2F5' }
              }}
            >
              {tab.label}
            </ButtonBase>
          );
        })}
      </Box>

      <Outlet context={{ merchant } satisfies LocationOutletContext} />
    </Box>
  );
};

export default LocationRoute;
